import type {
  CustomerRevenueReportItem,
  RevenuePerMonthBreakdown,
  YearlyRevenue,
} from '@vy/types';
import { entries, isNotNullOrUndefined } from '@vy/utils';
import type { RowTreeData } from '../../../../../hooks/use-tree-data';

const REVENUE_TYPES: readonly (keyof RevenuePerMonthBreakdown)[] = ['scheduledCharge', 'revenue', 'deferredRevenue', 'unbilledRevenue'];

export type WaterfallRevenueType = 'newDeferred' | 'recognizedDeferred' | 'endingDeferred' | 'newUnbilledAR' | 'recognizedUnbilled' | 'endingUnbilled' | 'spacer';

export type RevenueType = keyof RevenuePerMonthBreakdown | 'total' | WaterfallRevenueType;

export const ROW_LEVELS = {
  Customer: 'customer',
  Product: 'product',
  RevenueType: 'revenueType',
  Total: 'total',
  TotalChild: 'totalChild',
} as const;

export type RowLevel = (typeof ROW_LEVELS)[keyof typeof ROW_LEVELS];

export type ExpandedCustomerRevenueReportRow = CustomerRevenueReportItem &
  RowTreeData & {
    revenueType: RevenueType;
    level: RowLevel;
  };

type CustomerRevenueNameSource = Pick<
  CustomerRevenueReportItem,
  'product' | 'variant' | 'productVariant' | 'revisionedLineItem'
>;

// Single source of truth for the Product display name, shared by the grid
// column and the XLSX export so they can't drift apart. The real name lives on
// `variant.name`; the rest are fallbacks.
export function getCustomerRevenueProductName(
  source: CustomerRevenueNameSource,
  fallback = '-',
): string {
  return (
    source.variant?.name
    ?? source.product?.name
    ?? source.productVariant?.name
    ?? source.revisionedLineItem?.name
    ?? fallback
  );
}

export function expandCustomerRevenueReportItems(
  reportItems: CustomerRevenueReportItem[],
): ExpandedCustomerRevenueReportRow[] {
  const grouped = groupByCustomer(reportItems);
  const rows: ExpandedCustomerRevenueReportRow[] = [];

  for (const [customerId, items] of grouped) {
    const customerParentId = `customer-${customerId}`;

    for (const item of items) {
      const productId = buildProductId(item);
      const productParentId = `${customerParentId}-product-${productId}`;

      // ENG-6239: the customer grouping row was removed. Product rows are now
      // top-level (customer/contract are plain columns); each still expands to
      // its billed/revenue/deferred/unbilled breakdown.
      rows.push({
        ...item,
        revenueType: 'total',
        level: ROW_LEVELS.Product,
        id: productParentId,
        isParent: true,
        isExpanded: false,
      });

      for (const revenueType of REVENUE_TYPES) {
        rows.push({
          ...item,
          revenueType,
          level: ROW_LEVELS.RevenueType,
          id: `${productParentId}-${revenueType}`,
          isParent: false,
          isChild: true,
          parentId: productParentId,
          isExpanded: false,
          hidden: true,
        });
      }
    }
  }

  return rows;
}

function groupByCustomer(
  reportItems: CustomerRevenueReportItem[],
): Map<string, CustomerRevenueReportItem[]> {
  const map = new Map<string, CustomerRevenueReportItem[]>();

  for (const item of reportItems) {
    const existing = map.get(item.customer.id);

    if (isNotNullOrUndefined(existing)) {
      existing.push(item);
    } else {
      map.set(item.customer.id, [item]);
    }
  }

  return map;
}

function buildProductId(item: CustomerRevenueReportItem): string {
  const contractId = item.contract?.id ?? 'no-contract';
  const productId = item.product?.id ?? item.revisionedLineItem?.name ?? 'no-product';
  const variantId = item.variantId ?? item.productVariant?.id ?? 'no-variant';
  const invoiceId = item.invoiceId ?? item.invoices?.[0]?.id;
  const baseId = `${contractId}-${productId}-${variantId}`;

  if (isNotNullOrUndefined(invoiceId)) {
    return `${baseId}-${invoiceId}`;
  }

  return baseId;
}

const WATERFALL_REVENUE_TYPES: readonly WaterfallRevenueType[] = ['newDeferred', 'recognizedDeferred', 'endingDeferred'];

function isWaterfallType(revenueType: RevenueType): revenueType is WaterfallRevenueType {
  return WATERFALL_REVENUE_TYPES.includes(revenueType as WaterfallRevenueType);
}

export function getRevenueKeysForType(revenueType: RevenueType): (keyof RevenuePerMonthBreakdown)[] {
  if (revenueType === 'total') {
    return [...REVENUE_TYPES];
  }

  if (isWaterfallType(revenueType)) {
    return [];
  }

  return [revenueType];
}

export function aggregateYearlyRevenue(reportItems: CustomerRevenueReportItem[]): YearlyRevenue {
  const result: Record<string, Record<string, RevenuePerMonthBreakdown>> = {};

  for (const item of reportItems) {
    for (const [year, revenuePerMonth] of entries(item.yearlyRevenue)) {
      const yearKey = String(year);

      if (!result[yearKey]) {
        result[yearKey] = {};
      }

      for (const [month, breakdown] of entries(revenuePerMonth)) {
        if (!isNotNullOrUndefined(breakdown)) {
          continue;
        }

        if (!result[yearKey][month]) {
          result[yearKey][month] = {
            scheduledCharge: 0,
            revenue: 0,
            unbilledRevenue: 0,
            deferredRevenue: 0,
          };
        }

        result[yearKey][month].scheduledCharge += breakdown.scheduledCharge ?? 0;
        result[yearKey][month].revenue += breakdown.revenue ?? 0;
        result[yearKey][month].unbilledRevenue += breakdown.unbilledRevenue ?? 0;
        result[yearKey][month].deferredRevenue += breakdown.deferredRevenue ?? 0;
      }
    }
  }

  return result as YearlyRevenue;
}

const REVENUE_TYPE_LABELS: Record<RevenueType, string> = {
  scheduledCharge: 'Billed',
  revenue: 'Revenue',
  deferredRevenue: 'Deferred',
  unbilledRevenue: 'Unbilled',
  newDeferred: 'New Deferred Rev',
  recognizedDeferred: 'Recognized Revenue (Deferred)',
  endingDeferred: 'Ending Deferred Balance',
  spacer: '',
  newUnbilledAR: 'New Unbilled AR',
  recognizedUnbilled: 'Recognized Revenue (Unbilled)',
  endingUnbilled: 'Ending Unbilled AR',
  total: '-',
};

export function getRevenueTypeLabel(revenueType: RevenueType): string {
  return REVENUE_TYPE_LABELS[revenueType];
}
